import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../../state/state.interface';
import { IBaseResponse, IGetManyResponse } from '../../../../utilities/http-utilities.service';
import {
  IDeleteItemWorkInstructionAssignment,
  IGetItemWorkInstructionAssignment,
  IItemWorkInstructionAssignment,
  IWorkInstructionItemCard,
} from './link-work-instruction.interface';

export interface ILinkWorkInstructionState {
  assignmentLoading: ELoadStatus;
  dataLoading: ELoadStatus;
  workInstructions: IWorkInstructionItemCard[];
}

@Injectable()
export class LinkWorkInstructionStore extends ComponentStore<ILinkWorkInstructionState> {
  public readonly data$: Signal<IWorkInstructionItemCard[]> = this.selectSignal(
    (state: ILinkWorkInstructionState) => state.workInstructions ?? [],
  );

  readonly loadWorkInstructions = this.effect((trigger$: Observable<IGetItemWorkInstructionAssignment>) =>
    trigger$.pipe(
      switchMap((params) => {
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItemWorkInstructionAssignments(params).pipe(
          tapResponse(
            (assignments) => {
              if (!assignments?.data) {
                this.patchState({ dataLoading: ELoadStatus.failure, workInstructions: [] });

                return;
              }

              const workInstructions: IWorkInstructionItemCard[] = assignments.data
                .filter(
                  (itemWorkInstructionAssignment: IItemWorkInstructionAssignment) =>
                    itemWorkInstructionAssignment.guide,
                )
                .map((itemWorkInstructionAssignment: IItemWorkInstructionAssignment) => ({
                  ...itemWorkInstructionAssignment.guide,
                  assignmentId: itemWorkInstructionAssignment.id,
                }));

              this.patchState({ dataLoading: ELoadStatus.success, workInstructions });
            },
            (error) => {
              this.patchState({ dataLoading: ELoadStatus.failure });

              console.error('Error loading assignments', error);
            },
          ),
        );
      }),
    ),
  );

  readonly unassignWorkInstructions = this.effect((trigger$: Observable<IDeleteItemWorkInstructionAssignment>) =>
    trigger$.pipe(
      switchMap(({ id, startDateForRecurringUpdate }) => {
        this.patchState({ assignmentLoading: ELoadStatus.loading });
        let httpParams = new HttpParams();

        if (startDateForRecurringUpdate !== undefined) {
          httpParams = httpParams.set('startDateForRecurringUpdate', String(startDateForRecurringUpdate));
        }

        return this.deleteItemGuideAssignment(id, { params: httpParams }).pipe(
          tapResponse(
            () => {
              this.patchState({ assignmentLoading: ELoadStatus.success });
            },
            () => {
              this.patchState({ assignmentLoading: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  constructor(private readonly http: HttpClient) {
    super({
      assignmentLoading: ELoadStatus.initial,
      dataLoading: ELoadStatus.initial,
      workInstructions: [],
    });
  }

  private getItemWorkInstructionAssignments(
    params: IGetItemWorkInstructionAssignment,
  ): Observable<IGetManyResponse<IItemWorkInstructionAssignment>> {
    const httpParams: HttpParams = new HttpParams()
      .append('join', 'guide')
      .set('s', JSON.stringify({ $and: [{ boardId: { $eq: params.boardId } }, { itemId: { $eq: params.itemId } }] }));

    return this.http.get<IGetManyResponse<IItemWorkInstructionAssignment>>('item-guide-assignments', {
      params: httpParams,
    });
  }

  private deleteItemGuideAssignment(
    id: number,
    httpOptions: Parameters<HttpClient['get']>[1] = {},
  ): Observable<IBaseResponse<IItemWorkInstructionAssignment>> {
    return this.http.delete<IBaseResponse<IItemWorkInstructionAssignment>>(`item-guide-assignments/${id}`, httpOptions);
  }
}
